import { ImpactBolt, Text, TextColor, TextVariant } from '@vizit/atoms';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { MetricCardSkeleton } from '../metric-card-skeleton';
import { containerClasses, getAssetCountColor } from './styles';
import type { AssetCountStatus, AssetCountV2Props } from './types';

function getStatus(count: number, optimalMin: number, optimalMax: number): AssetCountStatus {
  return count >= optimalMin && count <= optimalMax ? 'success' : 'warning';
}

function formatOptimalRange(optimalMin: number, optimalMax: number): string {
  return optimalMin === optimalMax ? `${optimalMin} is optimal` : `${optimalMin}-${optimalMax} is optimal`;
}

/**
 * Asset count metric card displaying the current asset count
 * relative to an optimal range. The count number is green when
 * within `[optimalMin, optimalMax]`, orange otherwise.
 *
 * @example
 * <AssetCountV2 count={8} optimalMin={7} optimalMax={9} impactLevel="high" />
 */
const AssetCountV2 = React.forwardRef<HTMLDivElement, AssetCountV2Props>((props, forwardedRef) => {
  const {
    count,
    optimalMin,
    optimalMax,
    impactLevel,
    isDisabled = false,
    isFocused = false,
    isLoading = false,
    className,
    ...other
  } = props;

  if (isLoading) {
    return (
      <div
        ref={forwardedRef}
        className={cx(containerClasses({ state: 'default' }), className)}
        aria-busy="true"
        data-testid="asset-count-v2"
        {...other}
      >
        <MetricCardSkeleton />
      </div>
    );
  }

  const status = getStatus(count, optimalMin, optimalMax);
  const state = isDisabled ? 'disabled' : isFocused ? 'focused' : 'default';

  return (
    <div
      ref={forwardedRef}
      className={cx(containerClasses({ state }), className)}
      data-testid="asset-count-v2"
      data-state={state}
      {...other}
    >
      <div className="flex items-start justify-between w-full">
        <Text
          variant={TextVariant.MonoXs}
          color={TextColor.Quaternary}
          className={isDisabled ? 'text-fg-disabled-subtle' : ''}
          showEllipsis={false}
          data-testid="asset-count-name"
        >
          ASSET COUNT
        </Text>
        <ImpactBolt level={impactLevel} hideUnfilled isDisabled={isDisabled} />
      </div>

      <div className="flex flex-col gap-0.5">
        <Text
          variant={TextVariant.MonoXl}
          elementType="span"
          color={TextColor.Inherit}
          className={getAssetCountColor(status, isDisabled)}
          showEllipsis={false}
          data-testid="asset-count-value-calculated"
        >
          {count}
        </Text>
        <Text
          variant={TextVariant.MonoSm}
          color={TextColor.Tertiary}
          className={isDisabled ? 'text-fg-disabled-subtle' : ''}
          showEllipsis={false}
          data-testid="asset-count-value-optimal"
        >
          {formatOptimalRange(optimalMin, optimalMax)}
        </Text>
      </div>
    </div>
  );
});

AssetCountV2.displayName = 'AssetCountV2';

export { AssetCountV2 };
export type { AssetCountV2Props };
